import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { t } from '../../lib/i18n';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Input } from '../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import { Bug, Lightbulb, Trash2, MessageSquare, Search } from 'lucide-react';

const STATUS_COLORS = {
  new: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
  acknowledged: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20',
  in_progress: 'bg-purple-500/10 text-purple-500 border-purple-500/20',
  resolved: 'bg-green-500/10 text-green-500 border-green-500/20',
  closed: 'bg-gray-500/10 text-gray-500 border-gray-500/20',
};

const PRIORITY_COLORS = {
  low: 'bg-gray-500/10 text-gray-500 border-gray-500/20',
  medium: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
  high: 'bg-orange-500/10 text-orange-500 border-orange-500/20',
  critical: 'bg-red-500/10 text-red-500 border-red-500/20',
};

const AdminReportsPage = () => {
  const { language } = useAuth();
  const [reports, setReports] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [selectedReport, setSelectedReport] = useState(null);
  const [adminResponse, setAdminResponse] = useState('');
  const [updating, setUpdating] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Settings tab
  const [reportMode, setReportMode] = useState('all_users');
  const [savingSettings, setSavingSettings] = useState(false);
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [togglingUser, setTogglingUser] = useState(null);

  const fetchReports = async () => {
    try {
      const params = {};
      if (statusFilter && statusFilter !== 'all') params.status_filter = statusFilter;
      if (typeFilter && typeFilter !== 'all') params.type_filter = typeFilter;
      const res = await axios.get('/api/admin/reports', { params });
      setReports(res.data.reports);
      setTotal(res.data.total);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error loading reports');
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const res = await axios.get('/api/admin/reports-settings');
      setReportMode(res.data.report_mode);
    } catch { /* silent */ }
  };

  const fetchUsers = async () => {
    try {
      const res = await axios.get('/api/admin/users');
      setUsers(res.data.filter(u => !u.is_admin));
    } catch { /* silent */ }
  };

  useEffect(() => {
    fetchReports();
    fetchSettings();
  }, [statusFilter, typeFilter]);

  const handleUpdateReport = async (reportId, status, response) => {
    setUpdating(true);
    try {
      const data = {};
      if (status) data.status = status;
      if (response !== undefined) data.admin_response = response;
      const res = await axios.patch(`/api/admin/reports/${reportId}`, data);
      setReports(prev => prev.map(r => r.id === reportId ? res.data : r));
      setSelectedReport(res.data);
      toast.success(language === 'fr' ? 'Rapport mis à jour' : 'Report updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog) return;
    setDeleting(true);
    try {
      await axios.delete(`/api/admin/reports/${deleteDialog.id}`);
      setReports(prev => prev.filter(r => r.id !== deleteDialog.id));
      setTotal(prev => prev - 1);
      setDeleteDialog(null);
      if (selectedReport?.id === deleteDialog.id) setSelectedReport(null);
      toast.success(language === 'fr' ? 'Rapport supprimé' : 'Report deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setDeleting(false);
    }
  };

  const handleSaveSettings = async (mode) => {
    setSavingSettings(true);
    try {
      await axios.patch('/api/admin/reports-settings', { report_mode: mode });
      setReportMode(mode);
      toast.success(language === 'fr' ? 'Paramètres sauvegardés' : 'Settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleToggleUser = async (userId, currentValue) => {
    setTogglingUser(userId);
    try {
      await axios.patch(`/api/admin/users/${userId}/report-permissions`, {
        can_submit_reports: !currentValue,
      });
      setUsers(prev => prev.map(u =>
        u.id === userId ? { ...u, can_submit_reports: !currentValue } : u
      ));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setTogglingUser(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr + (dateStr.endsWith('Z') ? '' : 'Z')).toLocaleDateString(
      language === 'fr' ? 'fr-FR' : 'en-US',
      { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }
    );
  };

  const filteredUsers = users.filter(u =>
    u.username?.toLowerCase().includes(userSearch.toLowerCase()) ||
    u.email?.toLowerCase().includes(userSearch.toLowerCase())
  );

  return (
    <AdminLayout>
      <div className="space-y-6">
        <h1 className="text-2xl font-bold">{t('admin_reports', language)}</h1>

        <Tabs defaultValue="reports" onValueChange={(v) => { if (v === 'settings') { fetchUsers(); } }}>
          <TabsList>
            <TabsTrigger value="reports">{t('admin_reports', language)} ({total})</TabsTrigger>
            <TabsTrigger value="settings">{t('report_settings', language)}</TabsTrigger>
          </TabsList>

          {/* Reports Tab */}
          <TabsContent value="reports" className="space-y-4">
            {/* Filters */}
            <div className="flex flex-wrap gap-3">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder={language === 'fr' ? 'Tous les statuts' : 'All statuses'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{language === 'fr' ? 'Tous les statuts' : 'All statuses'}</SelectItem>
                  <SelectItem value="new">{t('report_status_new', language)}</SelectItem>
                  <SelectItem value="acknowledged">{t('report_status_acknowledged', language)}</SelectItem>
                  <SelectItem value="in_progress">{t('report_status_in_progress', language)}</SelectItem>
                  <SelectItem value="resolved">{t('report_status_resolved', language)}</SelectItem>
                  <SelectItem value="closed">{t('report_status_closed', language)}</SelectItem>
                </SelectContent>
              </Select>

              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder={language === 'fr' ? 'Tous les types' : 'All types'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{language === 'fr' ? 'Tous les types' : 'All types'}</SelectItem>
                  <SelectItem value="bug">{t('report_type_bug', language)}</SelectItem>
                  <SelectItem value="feature">{t('report_type_feature', language)}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Reports List */}
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">
                {language === 'fr' ? 'Chargement...' : 'Loading...'}
              </div>
            ) : reports.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                {t('report_no_reports', language)}
              </div>
            ) : (
              <div className="space-y-2">
                {reports.map((report) => (
                  <Card
                    key={report.id}
                    className="cursor-pointer hover:bg-accent/50 transition-colors"
                    onClick={() => { setSelectedReport(report); setAdminResponse(report.admin_response || ''); }}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          {report.type === 'bug' ? (
                            <Bug className="h-5 w-5 text-red-500 shrink-0" />
                          ) : (
                            <Lightbulb className="h-5 w-5 text-yellow-500 shrink-0" />
                          )}
                          <div className="min-w-0">
                            <p className="font-medium truncate">{report.title}</p>
                            <p className="text-sm text-muted-foreground">
                              {t('report_by', language)} {report.username} — {formatDate(report.created_at)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Badge variant="outline" className={PRIORITY_COLORS[report.priority] || ''}>
                            {t(`report_priority_${report.priority}`, language)}
                          </Badge>
                          <Badge variant="outline" className={STATUS_COLORS[report.status] || ''}>
                            {t(`report_status_${report.status}`, language)}
                          </Badge>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive"
                            onClick={(e) => { e.stopPropagation(); setDeleteDialog(report); }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>{t('report_mode_label', language)}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {['disabled', 'all_users', 'per_user'].map((mode) => (
                  <label
                    key={mode}
                    className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      reportMode === mode ? 'border-primary bg-primary/5' : 'border-border'
                    }`}
                  >
                    <input
                      type="radio"
                      name="report_mode"
                      value={mode}
                      checked={reportMode === mode}
                      onChange={() => handleSaveSettings(mode)}
                      disabled={savingSettings}
                      className="mt-1"
                    />
                    <div>
                      <p className="font-medium">{t(`report_mode_${mode}`, language)}</p>
                      <p className="text-sm text-muted-foreground">
                        {t(`report_mode_description_${mode}`, language)}
                      </p>
                    </div>
                  </label>
                ))}
              </CardContent>
            </Card>

            {reportMode === 'per_user' && (
              <Card>
                <CardHeader>
                  <CardTitle>{language === 'fr' ? 'Permissions par utilisateur' : 'Per-user permissions'}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      className="pl-9"
                      placeholder={language === 'fr' ? 'Rechercher un utilisateur...' : 'Search user...'}
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {filteredUsers.map((user) => (
                      <div key={user.id} className="flex items-center justify-between p-3 rounded-lg border">
                        <div>
                          <p className="font-medium">{user.username}</p>
                          <p className="text-sm text-muted-foreground">{user.email}</p>
                        </div>
                        <Switch
                          checked={!!user.can_submit_reports}
                          onCheckedChange={() => handleToggleUser(user.id, user.can_submit_reports)}
                          disabled={togglingUser === user.id}
                        />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Report Detail Dialog */}
      <Dialog open={selectedReport !== null} onOpenChange={(open) => { if (!open) setSelectedReport(null); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedReport?.type === 'bug' ? (
                <Bug className="h-5 w-5 text-red-500" />
              ) : (
                <Lightbulb className="h-5 w-5 text-yellow-500" />
              )}
              {t('report_detail', language)}
            </DialogTitle>
          </DialogHeader>

          {selectedReport && (
            <div className="space-y-4">
              <div>
                <p className="font-semibold text-lg">{selectedReport.title}</p>
                <p className="text-sm text-muted-foreground">
                  {t('report_by', language)} {selectedReport.username} — {formatDate(selectedReport.created_at)}
                </p>
              </div>

              <div className="flex gap-2">
                <Badge variant="outline" className={PRIORITY_COLORS[selectedReport.priority] || ''}>
                  {t(`report_priority_${selectedReport.priority}`, language)}
                </Badge>
                <Badge variant="outline" className={STATUS_COLORS[selectedReport.status] || ''}>
                  {t(`report_status_${selectedReport.status}`, language)}
                </Badge>
              </div>

              {selectedReport.description && (
                <div className="bg-muted/50 rounded-lg p-3">
                  <p className="text-sm whitespace-pre-wrap">{selectedReport.description}</p>
                </div>
              )}

              {/* Status Actions */}
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Changer le statut' : 'Change status'}</Label>
                <div className="flex flex-wrap gap-2">
                  {['acknowledged', 'in_progress', 'resolved', 'closed'].map((status) => (
                    <Button
                      key={status}
                      variant={selectedReport.status === status ? 'default' : 'outline'}
                      size="sm"
                      disabled={updating || selectedReport.status === status}
                      onClick={() => handleUpdateReport(selectedReport.id, status)}
                    >
                      {t(`report_status_${status}`, language)}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Admin Response */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" />
                  {t('report_admin_response', language)}
                </Label>
                <Textarea
                  value={adminResponse}
                  onChange={(e) => setAdminResponse(e.target.value)}
                  rows={3}
                  placeholder={language === 'fr' ? 'Écrire une réponse...' : 'Write a response...'}
                />
                <Button
                  size="sm"
                  disabled={updating || !adminResponse.trim()}
                  onClick={() => handleUpdateReport(selectedReport.id, null, adminResponse)}
                >
                  <MessageSquare className="mr-2 h-4 w-4" />
                  {t('report_admin_respond', language)}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog !== null} onOpenChange={(open) => { if (!open) setDeleteDialog(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{language === 'fr' ? 'Supprimer le rapport' : 'Delete report'}</DialogTitle>
            <DialogDescription>{t('report_delete_confirm', language)}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog(null)}>
              {t('cancel', language)}
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              {deleting ? '...' : (language === 'fr' ? 'Supprimer' : 'Delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminReportsPage;
